/**
 * API configuration settings
 */

// API base URL from environment variables
export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

// Search API base URL from environment variables
export const SEARCH_API_BASE_URL = import.meta.env.VITE_SEARCH_API_BASE_URL;
export const MCP_GATEWAY_BASE_URL = import.meta.env.VITE_MCP_GATEWAY_BASE_URL as string | undefined;

// API timeout settings
export const API_TIMEOUT = 10000; // 10 seconds

// Authentication settings
export const AUTH_TOKEN_KEY = 'authToken';
export const USER_DATA_KEY = 'userData';

// API endpoints
export const API_ENDPOINTS = {
  // Authentication endpoints (routed to asab-identity via APISIX)
  AUTH: {
    LOGIN: '/v1/auth/login',
    LOGOUT: '/v1/auth/logout',
    REGISTER: '/v1/auth/register',
    FORGOT_PASSWORD: '/v1/auth/forgot-password',
    RESET_PASSWORD: '/v1/auth/reset-password',
    VERIFY_EMAIL: '/v1/auth/verify-email',
    REQUEST_VERIFY_TOKEN: '/v1/auth/resend-verification',
    REFRESH: '/v1/auth/refresh',
  },

  // User endpoints (routed to asab-identity via APISIX)
  USERS: {
    ME: '/v1/users/me',
    DELETE: '/v1/users/me',
    BY_ID: (id: string) => `/v1/users/${id}`,
    // Current user profile (signup grant + payment method on file).
    ME_PROFILE: '/v1/users/me/profile',
  },

  // API Keys endpoints (routed to asab-identity via APISIX)
  API_KEYS: {
    LIST: (orgId: string) => `/v1/organizations/${orgId}/api-keys`,
    CREATE: (orgId: string) => `/v1/organizations/${orgId}/api-keys`,
    BY_ID: (orgId: string, id: string) => `/v1/organizations/${orgId}/api-keys/${id}`,
    UPDATE: (orgId: string, id: string) => `/v1/organizations/${orgId}/api-keys/${id}`,
    DELETE: (orgId: string, id: string) => `/v1/organizations/${orgId}/api-keys/${id}`,
    ROTATE: (orgId: string, id: string) => `/v1/organizations/${orgId}/api-keys/${id}/rotate`,
    PERMISSIONS: (orgId: string, id: string) =>
      `/v1/organizations/${orgId}/api-keys/${id}/permissions`,
    USAGE_STATS: (orgId: string, id: string) =>
      `/v1/organizations/${orgId}/api-keys/${id}/usage-stats`,

    // Role management (not yet implemented in asab-identity)
    ROLES: {
      LIST: (orgId: string) => `/v1/organizations/${orgId}/api-keys/roles`,
      CREATE: (orgId: string) => `/v1/organizations/${orgId}/api-keys/roles`,
    },

    // Permission catalog (categories, all permissions, role/scope mappings)
    PERMISSION_CATEGORIES: (orgId: string) =>
      `/v1/organizations/${orgId}/api-keys/permissions/categories`,

    // Permission check (not needed — APISIX Lua plugin handles this at the gateway)
    CHECK_PERMISSION: (orgId: string) => `/v1/organizations/${orgId}/api-keys/check-permission`,
  },

  // Health check
  HEALTH: '/health',

  // Organizations endpoints (routed to asab-identity via APISIX)
  ORGANIZATIONS: {
    LIST: '/v1/organizations',
    CREATE: '/v1/organizations',
    BY_ID: (id: string) => `/v1/organizations/${id}`,
    MEMBERS: (id: string) => `/v1/organizations/${id}/members`,
    MEMBER_ROLE: (id: string, userId: string) => `/v1/organizations/${id}/members/${userId}/role`,
    REMOVE_MEMBER: (id: string, userId: string) => `/v1/organizations/${id}/members/${userId}`,
    LEAVE: (id: string) => `/v1/organizations/${id}/leave`,
    INVITES: (id: string) => `/v1/organizations/${id}/invites`,
    ACCEPT_INVITE: (inviteId: string) => `/v1/organizations/invites/${inviteId}/accept`,
    CANCEL_INVITE: (id: string, inviteId: string) => `/v1/organizations/${id}/invites/${inviteId}`,
    DOMAINS: (id: string) => `/v1/organizations/${id}/domains`,
    DOMAIN_VERIFY: (id: string, domainId: string) =>
      `/v1/organizations/${id}/domains/${domainId}/verify`,
    DOMAIN_DELETE: (id: string, domainId: string) => `/v1/organizations/${id}/domains/${domainId}`,
    TRANSFER_OWNERSHIP: (id: string) => `/v1/organizations/${id}/transfer-ownership`,
    CREDITS: (id: string) => `/v1/organizations/${id}/credits`,
    // Live per-org scheduler status (served by asab-agents).
    SCHEDULER_STATUS: (id: string) => `/v1/organizations/${id}/scheduler/status`,
    // SSO add-on (asab-billing).
    SSO_ADDON: (id: string) => `/v1/billing/orgs/${id}/sso-addon`,
    // Org general settings (model_roles + future knobs) — served by asab-agents.
    SETTINGS: (id: string) => `/v1/organizations/${id}/settings`,
  },

  // Billing endpoints (asab-accounts — formerly asab-billing).
  BILLING: {
    PLANS: '/v1/billing/plans',
    PROVIDER: '/v1/billing/provider',
    SUBSCRIBE: '/v1/billing/subscribe',
    SEATS: '/v1/billing/seats',
    CORES: '/v1/billing/cores',
    SUBSCRIPTION: '/v1/billing/subscription',
    SSO_ADDON: '/v1/billing/sso-addon',
    TOPUP: '/v1/billing/topup',
    INVOICES: '/v1/billing/invoices',
    INVOICE_BY_ID: (id: string) => `/v1/billing/invoices/${id}`,
    PAYMENT_METHODS: '/v1/billing/payment-methods',
    PAYMENT_METHOD_BY_ID: (id: string) => `/v1/billing/payment-methods/${id}`,
  },

  ANALYTICS: {
    COSTS_SUMMARY: '/v1/analytics/costs/summary',
    EVENTS: '/v1/analytics/events',
    COSTS_BY_MODEL: '/v1/analytics/costs/by-model',
    USAGE_BY_API_KEY: '/v1/analytics/usage/by-api-key',
    USAGE_BY_USER: '/v1/analytics/usage/by-user',
    USAGE_BY_ACTION: '/v1/analytics/usage/by-action',
    USAGE_TOP_CONSUMERS: '/v1/analytics/usage/top-consumers',
    LATENCY_SUMMARY: '/v1/analytics/latency/summary',
    ERRORS_BY_MODEL: '/v1/analytics/errors/by-model',
    TOKENS_TRENDS: '/v1/analytics/tokens/trends',
    DASHBOARD_SUMMARY: '/v1/analytics/dashboard/summary',
    RESOURCE_COUNTS: '/v1/analytics/resource-counts',
    HEALTH_OVERVIEW: '/v1/analytics/health/overview',
    CREDITS_SUMMARY: '/v1/analytics/credits/summary',
    CREDITS_BY_SOURCE: '/v1/analytics/credits/by-source',
    AGENTS_TOP: '/v1/analytics/agents/top',
    TOOLS_TOP: '/v1/analytics/tools/top',
    EXECUTIONS_RATE: '/v1/analytics/executions/rate',
  },

  // Agent endpoints
  AGENTS: {
    LIST: '/v1/agents',
    CREATE: '/v1/agents',
    BY_ID: (agentId: string) => `/v1/agents/${agentId}`,
    UPDATE: (agentId: string) => `/v1/agents/${agentId}`,
    DELETE: (agentId: string) => `/v1/agents/${agentId}`,
    EXECUTE: '/v1/responses',
    ATTACH_MEMORIES: (agentId: string) => `/v1/agents/${agentId}/memories`,
    DETACH_MEMORY: (agentId: string, memoryId: string) => `/v1/agents/${agentId}/memories/${memoryId}`,
    ATTACH_GUARDRAILS: (agentId: string) => `/v1/agents/${agentId}/guardrails`,
    DETACH_GUARDRAIL: (agentId: string, guardrailId: string) => `/v1/agents/${agentId}/guardrails/${guardrailId}`,
  },

  // Event triggers — map an inbound event onto an agent run. Owner-scoped, not
  // org-scoped: the list returns the caller's own triggers, so these paths take
  // no org id. The public ingress path (/v1/events/{source}/{token}) is not here
  // because the browser never calls it — an external sender does, and the URL
  // comes back on the trigger record as ingress_url.
  EVENT_TRIGGERS: {
    LIST: '/v1/events/triggers',
    CREATE: '/v1/events/triggers',
    BY_ID: (id: string) => `/v1/events/triggers/${id}`,
    UPDATE: (id: string) => `/v1/events/triggers/${id}`,
    DELETE: (id: string) => `/v1/events/triggers/${id}`,
  },

  // Agent memory endpoints (org-scoped CRUD + org-default pins)
  MEMORIES: {
    LIST: (orgId: string) => `/v1/organizations/${orgId}/memories`,
    CREATE: (orgId: string) => `/v1/organizations/${orgId}/memories`,
    BY_ID: (orgId: string, id: string) => `/v1/organizations/${orgId}/memories/${id}`,
    UPDATE: (orgId: string, id: string) => `/v1/organizations/${orgId}/memories/${id}`,
    DELETE: (orgId: string, id: string) => `/v1/organizations/${orgId}/memories/${id}`,
    DEFAULTS_LIST: (orgId: string) => `/v1/organizations/${orgId}/memory/defaults`,
    DEFAULTS_PUT: (orgId: string, id: string) => `/v1/organizations/${orgId}/memory/defaults/${id}`,
    DEFAULTS_DELETE: (orgId: string, id: string) => `/v1/organizations/${orgId}/memory/defaults/${id}`,
  },

  // Agent skills endpoints (org-scoped CRUD + versions/rollback + org-default pins).
  // Note: skill-defaults is gated by agents:read/agents:write server-side, not
  // skills:read/skills:write (verified against asab-agents/cmd/server/main.go).
  SKILLS: {
    LIST: (orgId: string) => `/v1/organizations/${orgId}/skills`,
    CREATE: (orgId: string) => `/v1/organizations/${orgId}/skills`,
    CREATE_ARCHIVE: (orgId: string) => `/v1/organizations/${orgId}/skills/archive`,
    BY_ID: (orgId: string, id: string) => `/v1/organizations/${orgId}/skills/${id}`,
    UPDATE: (orgId: string, id: string) => `/v1/organizations/${orgId}/skills/${id}`,
    UPDATE_ARCHIVE: (orgId: string, id: string) => `/v1/organizations/${orgId}/skills/${id}/archive`,
    ROLLBACK: (orgId: string, id: string) => `/v1/organizations/${orgId}/skills/${id}/rollback`,
    DELETE: (orgId: string, id: string) => `/v1/organizations/${orgId}/skills/${id}`,
    FILE: (orgId: string, id: string, path: string) => `/v1/organizations/${orgId}/skills/${id}/files/${path}`,
    DEFAULTS_LIST: (orgId: string) => `/v1/organizations/${orgId}/skill-defaults`,
    DEFAULTS_PUT: (orgId: string, id: string) => `/v1/organizations/${orgId}/skill-defaults/${id}`,
    DEFAULTS_DELETE: (orgId: string, id: string) => `/v1/organizations/${orgId}/skill-defaults/${id}`,
  },

  // Standalone guardrail endpoints (org-scoped CRUD + enable toggle + rollback + org-default pins).
  GUARDRAILS: {
    LIST: (orgId: string) => `/v1/organizations/${orgId}/guardrails`,
    CREATE: (orgId: string) => `/v1/organizations/${orgId}/guardrails`,
    BY_ID: (orgId: string, id: string) => `/v1/organizations/${orgId}/guardrails/${id}`,
    UPDATE: (orgId: string, id: string) => `/v1/organizations/${orgId}/guardrails/${id}`,
    PATCH_ENABLED: (orgId: string, id: string) => `/v1/organizations/${orgId}/guardrails/${id}`,
    ROLLBACK: (orgId: string, id: string) => `/v1/organizations/${orgId}/guardrails/${id}/rollback`,
    DELETE: (orgId: string, id: string) => `/v1/organizations/${orgId}/guardrails/${id}`,
    DEFAULTS_LIST: (orgId: string) => `/v1/organizations/${orgId}/guardrail-defaults`,
    DEFAULTS_PUT: (orgId: string, id: string) => `/v1/organizations/${orgId}/guardrail-defaults/${id}`,
    DEFAULTS_DELETE: (orgId: string, id: string) => `/v1/organizations/${orgId}/guardrail-defaults/${id}`,
  },

  // Kernel endpoints (routed to asab-agents via APISIX)
  KERNEL: {
    STATUS: '/v1/kernel/status',
    PROCESSES: '/v1/kernel/processes',
    EXECUTIONS: '/v1/kernel/executions',
    EXECUTION: (taskId: string) => `/v1/kernel/executions/${taskId}`,
    SESSIONS: (agentId: string) => `/v1/agents/${agentId}/sessions`,
    SESSION: (agentId: string, sessionId: string) => `/v1/agents/${agentId}/sessions/${sessionId}`,
    REATTACH: (agentId: string, sessionId: string) => `/v1/agents/${agentId}/sessions/${sessionId}/reattach`,
    MEMORY: (agentId: string, sessionId: string) => `/v1/agents/${agentId}/sessions/${sessionId}/memory`,
    HANDOFFS: (agentId: string, sessionId: string) => `/v1/agents/${agentId}/sessions/${sessionId}/handoffs`,
    WORKFLOWS: '/v1/workflows',
    WORKFLOW: (workflowId: string) => `/v1/workflows/${workflowId}`,
    WORKFLOW_EXECUTE: (workflowId: string) => `/v1/workflows/${workflowId}/execute`,
    WORKFLOW_STREAM: (workflowId: string) => `/v1/workflows/${workflowId}/stream`,
    MESSAGES: '/v1/messages',
    MESSAGE: (messageId: string) => `/v1/messages/${messageId}`,
    ROUTES: '/v1/routes',
    ROUTE: (routeId: string) => `/v1/routes/${routeId}`,
    HOOKS: (agentId: string) => `/v1/agents/${agentId}/hooks`,
    HOOK: (agentId: string, hookId: string) => `/v1/agents/${agentId}/hooks/${hookId}`,
    HOOK_TEST: (agentId: string, hookId: string) => `/v1/agents/${agentId}/hooks/${hookId}/test`,
    BREAKPOINTS: (agentId: string) => `/v1/agents/${agentId}/breakpoints`,
    BREAKPOINT: (agentId: string, breakpointId: string) => `/v1/agents/${agentId}/breakpoints/${breakpointId}`,
    WEBHOOKS: '/v1/kernel/events/webhooks',
    WEBHOOK: (webhookId: string) => `/v1/kernel/events/webhooks/${webhookId}`,
    THOUGHT_STREAM: (agentId: string, sessionId: string) => `/v1/agents/${agentId}/sessions/${sessionId}/thoughts/stream`,
    EVENT_STREAM: '/v1/kernel/events/stream',
    VERSIONS: (agentId: string) => `/v1/agents/${agentId}/versions`,
    VERSION: (agentId: string, versionId: string) => `/v1/agents/${agentId}/versions/${versionId}`,
    VERSION_DIFF: (agentId: string, fromVersion: string, toVersion: string) =>
      `/v1/agents/${agentId}/versions/diff?from=${fromVersion}&to=${toVersion}`,
    TEMPLATES: '/v1/kernel/workflows/templates',
    TEMPLATE: (templateId: string) => `/v1/kernel/workflows/templates/${templateId}`,
    TEMPLATE_FORK: (templateId: string) => `/v1/kernel/workflows/templates/${templateId}/fork`,
    REPLAY: (sessionId: string) => `/v1/sessions/${sessionId}/replay`,
    REPLAY_START: (sessionId: string) => `/v1/sessions/${sessionId}/replay/start`,
    COST: (agentId: string) => `/v1/kernel/analytics/agents/${agentId}/cost`,
    FLEET_HEALTH: '/v1/kernel/analytics/fleet/health',
    FLAMEGRAPH: (agentId: string, sessionId: string) => `/v1/agents/${agentId}/sessions/${sessionId}/flamegraph`,
  },

  // Marketplace endpoints
  MARKETPLACE: {
    LIST: '/v1/marketplace/agents',
    REGISTER: '/v1/marketplace/agents',
    AGENT: (agentId: string) => `/v1/marketplace/agents/${agentId}`,
    CARD: (agentId: string) => `/v1/marketplace/agents/${agentId}/card`,
    UNREGISTER: (agentId: string) => `/v1/marketplace/agents/${agentId}`,
  },

  // Approvals endpoints
  APPROVALS: {
    LIST: '/v1/approvals',
    APPROVE: (approvalId: string) => `/v1/approvals/${approvalId}/approve`,
    REJECT: (approvalId: string) => `/v1/approvals/${approvalId}/reject`,
    STREAM: '/v1/approvals/stream',
  },

  // Authenticated route test
  AUTHENTICATED: '/authenticated-route',

  // Execution traces (served by asab-analytics — stored/historical)
  EXECUTIONS: {
    LIST: '/v1/executions',
    BY_ID: (taskId: string) => `/v1/executions/${taskId}`,
    // Live SSE observer — served by asab-agents, NOT asab-analytics, despite the
    // shared /v1/executions prefix (APISIX disambiguates by the more specific
    // /v1/executions/*/stream route). See CLAUDE.md's execution-trace architecture note.
    STREAM: (taskId: string) => `/v1/executions/${taskId}/stream`,
    // Live observability console (served by asab-agents). Under /v1/live/* to
    // stay clear of the /v1/executions/* namespace shared with asab-analytics.
    LIVE: '/v1/live/executions',
    STEER: (taskId: string) => `/v1/live/executions/${taskId}/steer`,
  },

  // Agent run control callbacks (served by asab-agents)
  TOOL_CALLBACKS: {
    RESULTS: '/v1/tool-results',
    APPROVALS: '/v1/tool-approvals',
  },
};

// HTTP status codes
export const HTTP_STATUS = {
  OK: 200,
  CREATED: 201,
  NO_CONTENT: 204,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  UNPROCESSABLE_ENTITY: 422,
  TOO_MANY_REQUESTS: 429,
  INTERNAL_SERVER_ERROR: 500,
} as const;

// Request retry settings
export const RETRY_CONFIG = {
  MAX_RETRIES: 3,
  INITIAL_DELAY: 1000, // 1 second
  MAX_DELAY: 5000, // 5 seconds
} as const;

// File upload settings
export const UPLOAD_CONFIG = {
  MAX_FILE_SIZE: 10 * 1024 * 1024, // 10MB
  ALLOWED_FILE_TYPES: ['image/jpeg', 'image/png', 'image/gif', 'application/pdf'],
} as const;
